import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import instance from "./index";
import { store } from "../redux/store";
import { refreshToken, logout } from "../redux/login";
import { updateTokens, deleteUserOnCookie } from "../cookies/cookies";
import { hideLoader } from "../redux/quantomState";

let refreshTokenPromise: Promise<string> | null = null;
let isLoggingOut = false;

const setUpInterceptor = (): void => {
  // Set up request interceptor
  instance.interceptors.request.use(
    (config: any) => {
      const token = store.getState().login.token;

      // If token exists, add it to the request's Authorization header
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }

      return config;
    },
    (error: any) => {
      return Promise.reject(error);
    }
  );

  // Set up response interceptor
  instance.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      const originalRequest = error.config;

      // Don't try to refresh if we're already logging out or if it's a logout/refresh request
      const isLogoutRequest = originalRequest.url?.includes("/logout");
      const isRefreshRequest = originalRequest.url?.includes("/refresh");

      // If the error is 401 on a refresh request, logout the user
      if (error.response?.status === 401 && isRefreshRequest) {
        // Prevent infinite loop - if we're already logging out, just reject
        if (isLoggingOut) {
          return Promise.reject(error);
        }

        // If we're refreshing and the refresh itself failed, clear the promise
        // All waiting requests will reject automatically when the promise rejects
        if (refreshTokenPromise) {
          refreshTokenPromise = null;
        }

        // Set logging out flag to prevent loops
        isLoggingOut = true;
        try {
          // Clean up local state immediately (cookies, localStorage)
          // This ensures cleanup happens even if logout API call fails
          deleteUserOnCookie();

          // Clear any open loaders to prevent UI freeze
          store.dispatch(hideLoader());

          // Dispatch logout action (will reset Redux state even if API call fails)
          // Don't await it to avoid blocking, but it will handle cleanup
          store.dispatch(logout()).catch((logoutError) => {
            // Logout API call failed, but cleanup already happened
            console.error("Logout API call failed:", logoutError);
          });
        } catch (cleanupError) {
          // Even if cleanup fails, we should still reject the original error
          console.error("Cleanup failed:", cleanupError);
          // Ensure loader is cleared even if cleanup fails
          store.dispatch(hideLoader());
        } finally {
          isLoggingOut = false;
        }
        return Promise.reject(error);
      }

      // If the error is 401 and we haven't tried to refresh the token yet
      // Also make sure we're not already trying to refresh the token itself or logging out
      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !isRefreshRequest &&
        !isLogoutRequest &&
        !isLoggingOut
      ) {
        // If we're already refreshing, wait for the existing refresh promise
        if (refreshTokenPromise) {
          return refreshTokenPromise
            .then((token) => {
              // Update the original request with new token
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return instance(originalRequest);
            })
            .catch((err) => {
              // If refresh failed, ensure loader is cleared for this request
              // The interceptor will handle logout, but we need to ensure loader is cleared
              store.dispatch(hideLoader());
              return Promise.reject(err);
            });
        }

        // Mark this request as retried to prevent infinite loops
        originalRequest._retry = true;

        // Create a new refresh promise that all concurrent requests will wait for
        refreshTokenPromise = (async () => {
          try {
            // Dispatch refresh token action
            const result = await store.dispatch(refreshToken());

            if (refreshToken.fulfilled.match(result)) {
              const { token } = result.payload;

              // Update access token in cookies
              // Note: Refresh token is handled by HTTP-only cookies from backend
              updateTokens(token);

              return token;
            } else {
              // Refresh failed - clear loader and logout
              store.dispatch(hideLoader());
              throw error;
            }
          } catch (refreshError) {
            // Refresh failed - clear loader to prevent UI freeze
            store.dispatch(hideLoader());
            throw refreshError;
          } finally {
            // Clear the refresh promise so future requests can trigger a new refresh if needed
            refreshTokenPromise = null;
          }
        })();

        // Wait for the refresh to complete, then retry the original request
        try {
          const token = await refreshTokenPromise;

          // Update the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }

          // Retry the original request
          return instance(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

export default setUpInterceptor;
